import type { ValueFormatterParams } from "ag-grid-community";
import type { TCell, TData } from "@package/grid-utils";
import { parseNumber } from "../../numbers";

type ValueFormatter = (params: Pick<ValueFormatterParams<TData, TCell>, "value">) => string;

const standardValueFormatter = (formatter: Intl.NumberFormat) => (number: number) => {
  return formatter.format(number);
};

const displayNegativeBracketFormatter = (formatter: Intl.NumberFormat) => (number: number) => {
  const formatted = formatter.format(number);
  return formatted.startsWith("-") ? `(${formatted.slice(1)})` : formatted;
};

const DATE_FIELD_SELECTORS = new Set(["expiry", "oidate"]);

export const columnValueFormatter = ({
  columnFieldSelector,
  displayNegativeBracket,
  formatter,
  uom,
  productSettingsLoading,
}: {
  columnFieldSelector: string | null | undefined;
  displayNegativeBracket: boolean;
  formatter: Intl.NumberFormat;
  uom?: string | null;
  productSettingsLoading?: boolean;
}): ValueFormatter => {
  // If product settings are still loading, return empty string.
  if (productSettingsLoading) {
    return () => "";
  }

  if (columnFieldSelector === "lasttime") {
    return ({ value: cellValue }) => {
      if (cellValue === null || cellValue === undefined) {
        return "";
      }
      const date = new Date(cellValue);
      return date.toTimeString().split(" ")[0];
    };
  }

  if (columnFieldSelector && DATE_FIELD_SELECTORS.has(columnFieldSelector)) {
    return ({ value: cellValue }) => {
      if (cellValue === null || cellValue === undefined) return "";
      const date = new Date(cellValue);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };
  }

  const numberFormatter = displayNegativeBracket
    ? displayNegativeBracketFormatter(formatter)
    : standardValueFormatter(formatter);

  const isPercent = uom === "percent";

  return ({ value: cellValue }) => {
    if (cellValue === null || cellValue === undefined) {
      return "";
    }

    if (typeof cellValue === "string") return cellValue;

    const number = parseNumber(cellValue);
    if (number === null) return "";

    const valueToFormat = isPercent ? cellValue * 100 : cellValue;

    const formatted = numberFormatter(valueToFormat);

    return isPercent ? `${formatted}%` : formatted;
  };
};
